import type { JSX, ReactNode } from 'react';
import { useApp, type Screen } from '../state/app.js';
import { Sidebar } from './Sidebar.js';
import { Topbar } from './Topbar.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { IconAdmin, IconApprove, IconCompany, IconHome, IconLeave, IconStats, IconTrip, IconWeekly, IconExpense, IconOvertime } from '../ui/icons.js';

function PaneTabs({ pendingCount }: { pendingCount: number }): JSX.Element {
  const { me, screen, setScreen } = useApp();
  const tabs: Array<{ s: Screen; icon: ReactNode; label: string; show: boolean; badge?: number }> = [
    { s: 'home', icon: <IconHome size={18} />, label: '홈', show: true },
    { s: 'admin', icon: <IconAdmin size={18} />, label: '근무', show: true },
    { s: 'overtime', icon: <IconOvertime size={18} />, label: '야근', show: true },
    { s: 'leave', icon: <IconLeave size={18} />, label: '휴가', show: true },
    { s: 'trip', icon: <IconTrip size={18} />, label: '출장', show: true },
    { s: 'weekly', icon: <IconWeekly size={18} />, label: '주간', show: true },
    { s: 'expense', icon: <IconExpense size={18} />, label: '경비', show: true },
    { s: 'company', icon: <IconCompany size={18} />, label: '회사', show: true },
    { s: 'approve', icon: <IconApprove size={18} />, label: '승인', show: me.permissions.canApproveLeave, badge: pendingCount },
    { s: 'stats', icon: <IconStats size={18} />, label: '통계', show: me.permissions.canViewAdmin },
  ];
  return (
    <div style={{ flexShrink: 0, borderTop: '1px solid #EDEFF2', display: 'flex', padding: '6px 4px', background: '#fff' }}>
      {tabs.filter((t) => t.show).map((t) => (
        <button key={t.s} onClick={() => setScreen(t.s)} style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, border: 'none', background: 'transparent',
          fontFamily: 'inherit', fontSize: 10.5, fontWeight: 600, padding: '5px 2px', cursor: 'pointer', borderRadius: 8,
          color: screen === t.s ? '#2563EB' : '#9CA3AF', position: 'relative',
        }}>
          {t.icon}{t.label}
          {t.badge ? <span style={{ position: 'absolute', top: 2, right: '50%', marginRight: -18, background: '#EF4444', color: '#fff', fontSize: 9, fontWeight: 700, minWidth: 14, height: 14, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Shell({ pendingCount = 0, children }: { pendingCount?: number; children: ReactNode }): JSX.Element {
  const { env } = useApp();
  const isMobile = useIsMobile();

  // 스마트폰 브라우저(비 Outlook): 사이드바 제거 → 전체화면 컬럼 + 하단 탭바
  if (env !== 'pane' && isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#F3F4F6' }}>
        <Topbar />
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch', padding: 12, background: '#F3F4F6' }}>{children}</div>
        <PaneTabs pendingCount={pendingCount} />
      </div>
    );
  }

  if (env === 'pane') {
    return (
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', background: '#E9EBEF', overflow: 'auto' }}>
        <div style={{ width: 384, maxWidth: '100%', height: 752, maxHeight: 'calc(100vh - 24px)', background: '#fff', border: '1px solid #D1D5DB', borderRadius: 16, overflow: 'hidden', margin: '12px auto', display: 'flex', flexDirection: 'column', boxShadow: 'var(--pane-shadow)' }}>
          <Topbar />
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, background: '#F3F4F6' }}>{children}</div>
          <PaneTabs pendingCount={pendingCount} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0, height: '100vh', background: '#F3F4F6' }}>
      <Sidebar pendingCount={pendingCount} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Topbar />
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 18, background: '#F3F4F6' }}>{children}</div>
      </div>
    </div>
  );
}
